import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";
import { CHANNEL_CODES, saveNotificationChannel } from "@/lib/catalog-api";
import { useConfiguration } from "../hooks/useConfiguration";
import { useToast } from "../hooks/useToast";
import { CHANNEL_LABELS } from "../lib/steps";
import { saveErrorMessage } from "../lib/saveMessage";

const CHANNEL_CREATE_LABELS: Record<string, string> = {
  EMAIL: "Email",
  SMS: "SMS",
  APP: "Andor staff app",
  API: "Third-party / Epic Rover API",
};

export function NotificationsStep() {
  const { channels, upsertChannel, setSaving, saving } = useConfiguration();
  const toast = useToast();
  const channelCoverage = useMemo(() => {
    const codes = new Set(channels.map((channel) => channel.channelCode.toUpperCase()));
    return CHANNEL_CODES.map((code) => ({
      code,
      channel:
        channels.find((item) => item.channelCode.toUpperCase() === code) ??
        null,
      present: codes.has(code),
    }));
  }, [channels]);

  const ensureChannel = async (code: string) => {
    const existing = channels.find(
      (channel) => channel.channelCode.toUpperCase() === code,
    );
    setSaving(true);
    try {
      const result = await saveNotificationChannel(
        {
          channelCode: code,
          displayName: existing?.displayName || CHANNEL_CREATE_LABELS[code] || code,
          isEnabled: true,
          configJson: existing?.configJson ?? null,
        },
        existing?.notificationChannelId,
      );
      if (!result.ok || !result.channel) {
        toast.error(saveErrorMessage(result.message));
        return;
      }
      upsertChannel(result.channel);
      toast.success(`${CHANNEL_LABELS[code] ?? code} is on`);
    } catch (error) {
      toast.error(saveErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      {channelCoverage.map(({ code, channel, present }) => (
        <Box
          key={code}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            border: "1px solid #e4e7ec",
            p: 1.5,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700 }}>
              {CHANNEL_LABELS[code] ?? code}
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: "0.9rem" }}>
              {channel ? (channel.isEnabled ? "On" : "Off") : "Off"}
            </Typography>
          </Box>
          <Button
            size="small"
            variant={present && channel?.isEnabled ? "outlined" : "contained"}
            disabled={saving}
            onClick={() => void ensureChannel(code)}
          >
            {present && channel?.isEnabled ? "On" : "Turn on"}
          </Button>
        </Box>
      ))}
    </Stack>
  );
}
